import {
  TwiplaPlayer,
  type CompactionRule,
  type ComputeCustomEventDetails,
  type CustomizationProps,
  type EventPipelineProps,
  type MergeConditionsMap,
  type ParsedRecordingEvent,
  type PlayerBehaviorProps,
  type Recording,
  type TwiplaPlayerHandle,
} from '@twipla/recordings-player';
import { forwardRef, ReactElement, ReactNode } from 'react';
import {
  computeCustomEventDetails,
  EVENTS_COMPACT_CONFIGURATION,
  EVENTS_MERGE_CONFIGURATION,
  getEventIcon,
} from './helpers';

export type SSRPlayerProps = {
  recordings: Recording[];
  events: Record<string, ParsedRecordingEvent[]>;
  errorNode?: ReactNode | ((args: { message: string; status?: string }) => ReactElement);
  getEventText?: (name: string, extras: unknown) => string;
  getEventTooltip?: (name: string, extras: unknown) => string;
} & Omit<
  EventPipelineProps,
  'computeCustomEventDetails' | 'compactionConfig' | 'mergeConfig' | 'getEventText' | 'getEventTooltip'
> &
  Omit<PlayerBehaviorProps, 'getEventIcon'> &
  Omit<CustomizationProps, 'errorNode'>;

export const SSRPlayer = forwardRef<TwiplaPlayerHandle, SSRPlayerProps>(function SSRPlayer(
  { errorNode, getEventText, getEventTooltip, ...props },
  ref,
) {
  const normalizedErrorNode =
    typeof errorNode === 'function'
      ? (errorNode as (args: { message: string; status?: string }) => ReactElement)
      : errorNode != null
        ? () => errorNode as ReactElement
        : undefined;

  const bridgedGetEventText = getEventText
    ? (name: string | undefined, extras: unknown) => getEventText(name ?? '', extras)
    : undefined;
  const bridgedGetEventTooltip = getEventTooltip
    ? (name: string | undefined, extras: unknown) => getEventTooltip(name ?? '', extras)
    : undefined;

  return (
    <TwiplaPlayer
      ref={ref}
      computeCustomEventDetails={computeCustomEventDetails as unknown as ComputeCustomEventDetails}
      compactionConfig={EVENTS_COMPACT_CONFIGURATION as unknown as CompactionRule[]}
      mergeConfig={EVENTS_MERGE_CONFIGURATION as unknown as MergeConditionsMap}
      getEventIcon={getEventIcon}
      getEventText={bridgedGetEventText}
      getEventTooltip={bridgedGetEventTooltip}
      errorNode={normalizedErrorNode}
      {...props}
    />
  );
});

export default SSRPlayer;
